import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useTinyTimResolvedContext } from './TinyTimContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TinyTimChat = ({ user }) => {
  const { page, page_context } = useTinyTimResolvedContext();
  const operatorId = user?.operator_id || null;
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('tim_chat_open') === 'true'; } catch { return false; }
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem('tim_session_id') || null; } catch { return null; }
  });
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [provider, setProvider] = useState(null);
  const [lastIntent, setLastIntent] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Persist open/session state
  useEffect(() => {
    try { localStorage.setItem('tim_chat_open', open ? 'true' : 'false'); } catch {}
  }, [open]);

  useEffect(() => {
    try {
      if (sessionId) localStorage.setItem('tim_session_id', sessionId);
      else localStorage.removeItem('tim_session_id');
    } catch {}
  }, [sessionId]);

  // Load session history on mount if we have a persisted sessionId
  useEffect(() => {
    if (sessionId) {
      loadSession(sessionId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open && !sessionId) loadSessions();
  }, [open, sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const loadSessions = async () => {
    try {
      const res = await axios.get(`${API}/tiny-tim/sessions?limit=10`);
      setSessions(res.data.sessions || []);
    } catch (e) { console.error(e); }
  };

  const loadSession = async (sid) => {
    try {
      const res = await axios.get(`${API}/tiny-tim/sessions/${sid}`);
      const msgs = res.data.messages || [];
      setMessages(msgs);
      setSessionId(sid);
      setShowSessions(false);
      if (msgs.length > 0) {
        // Detect provider from last assistant message
        const lastAssistant = [...msgs].reverse().find(m => m.role === 'assistant');
        if (lastAssistant) setProvider(lastAssistant.provider || null);
      }
    } catch (e) { console.error(e); }
  };

  const startNewSession = () => {
    setSessionId(null);
    setMessages([]);
    setShowSessions(false);
    setProvider(null);
    try { localStorage.removeItem('tim_session_id'); } catch {}
  };

  const sendMessage = useCallback(async (override) => {
    const text = (override ?? input).trim();
    if (!text || sending) return;
    if (!override) setInput('');
    setSending(true);

    const userMsg = { role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);

    try {
      const res = await axios.post(`${API}/tiny-tim/message`, {
        message: text,
        session_id: sessionId || undefined,
        source: 'dashboard',
        operator_id: operatorId || undefined,
        page: page || undefined,
        page_context: page_context || undefined,
      });
      const data = res.data;
      if (!sessionId) setSessionId(data.session_id);
      setProvider(data.provider);
      setLastIntent(data.intent || null);

      const assistantMsg = {
        role: 'assistant',
        content: data.response,
        created_at: new Date().toISOString(),
        intent: data.intent,
        suggested_tool: data.suggested_tool,
      };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (e) {
      const errMsg = { role: 'assistant', content: 'Connection error. Try again.', created_at: new Date().toISOString() };
      setMessages(prev => [...prev, errMsg]);
    } finally {
      setSending(false);
    }
  }, [input, sending, sessionId, operatorId, page, page_context]);

  // External call sites (e.g. WinnerRotatorPanel "Generate Scaling Brief")
  // can dispatch `cipher:tinytim-prompt` with `{ detail: { prompt } }` to
  // open the chat panel and auto-send a pre-loaded prompt.
  useEffect(() => {
    const handler = (e) => {
      const prompt = e?.detail?.prompt;
      if (!prompt) return;
      setOpen(true);
      // tiny delay so the panel is mounted before send fires
      setTimeout(() => sendMessage(prompt), 50);
    };
    window.addEventListener('cipher:tinytim-prompt', handler);
    return () => window.removeEventListener('cipher:tinytim-prompt', handler);
  }, [sendMessage]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="tiny-tim-toggle"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
        style={{
          background: 'linear-gradient(135deg, #00F0FF 0%, #B026FF 100%)',
          boxShadow: '0 0 20px rgba(0, 240, 255, 0.4), 0 0 40px rgba(176, 38, 255, 0.2)',
        }}
      >
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      </button>
    );
  }

  return (
    <div
      data-testid="tiny-tim-panel"
      className="fixed bottom-6 right-6 z-50 flex flex-col"
      style={{
        width: '400px',
        height: '560px',
        background: 'linear-gradient(160deg, rgba(0, 240, 255, 0.08) 0%, rgba(19, 56, 66, 0.95) 20%, rgba(15, 45, 55, 0.98) 100%)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(0, 240, 255, 0.25)',
        borderRadius: '16px',
        boxShadow: '0 0 30px rgba(0, 240, 255, 0.15), 0 20px 60px rgba(0, 0, 0, 0.5)',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: 'rgba(0, 240, 255, 0.15)' }}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
            style={{ background: 'linear-gradient(135deg, #00F0FF, #B026FF)', color: '#fff' }}>
            TT
          </div>
          <div>
            <span className="text-sm font-semibold text-white">Tiny Tim</span>
            {provider && (
              <span className="ml-2 text-xs px-1.5 py-0.5 rounded" style={{
                background: provider === 'ollama' ? 'rgba(0, 240, 255, 0.15)' : 'rgba(176, 38, 255, 0.15)',
                color: provider === 'ollama' ? '#00F0FF' : '#D279FF',
              }}>
                {provider === 'ollama' ? 'Local' : 'Claude'}
              </span>
            )}
            {lastIntent && (
              <span
                data-testid="tiny-tim-intent-badge"
                className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded font-mono"
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  color: '#A6E5E5',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                }}
                title={`Last classified intent: ${lastIntent}`}
              >
                {lastIntent}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setShowSessions(!showSessions)}
            data-testid="tiny-tim-sessions-btn"
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            title="Sessions"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </button>
          <button
            onClick={startNewSession}
            data-testid="tiny-tim-new-session-btn"
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
            title="New session"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
          <button
            onClick={() => setOpen(false)}
            data-testid="tiny-tim-close-btn"
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Sessions dropdown */}
      {showSessions && (
        <div className="px-3 py-2 border-b max-h-40 overflow-y-auto" style={{ borderColor: 'rgba(0, 240, 255, 0.1)', background: 'rgba(0, 0, 0, 0.2)' }}>
          <button
            onClick={startNewSession}
            className="w-full text-left px-3 py-2 rounded-lg text-xs font-medium mb-1 transition-colors"
            style={{ color: '#00F0FF' }}
          >
            + New conversation
          </button>
          {sessions.map((s) => (
            <button
              key={s.session_id}
              onClick={() => loadSession(s.session_id)}
              data-testid={`session-${s.session_id}`}
              className="w-full text-left px-3 py-1.5 rounded-lg text-xs text-slate-300 hover:bg-white/5 transition-colors truncate"
            >
              {s.session_id.startsWith('tg_') ? 'Telegram' : 'Dashboard'} - {s.message_count} msgs
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="tiny-tim-messages">
        {messages.length === 0 && (
          <div className="text-center py-12">
            <div className="text-3xl mb-3">TT</div>
            <p className="text-sm text-slate-400">What do you need, Nick?</p>
            <p className="text-xs text-slate-500 mt-1">Ask about offers, campaigns, or the business.</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              data-testid={`tim-msg-${i}`}
              className="max-w-[85%] rounded-xl px-3 py-2.5 text-sm"
              style={{
                background: msg.role === 'user'
                  ? 'rgba(0, 240, 255, 0.1)'
                  : 'rgba(255, 255, 255, 0.04)',
                border: msg.role === 'user'
                  ? '1px solid rgba(0, 240, 255, 0.2)'
                  : '1px solid rgba(255, 255, 255, 0.06)',
                color: msg.role === 'user' ? '#B8F0F0' : '#D4E0E0',
              }}
            >
              <div className="whitespace-pre-wrap leading-relaxed text-[13px]">{msg.content}</div>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-xl px-3 py-2.5 text-sm" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="flex gap-1">
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#00F0FF', animationDelay: '0ms' }} />
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#00F0FF', animationDelay: '200ms' }} />
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: '#00F0FF', animationDelay: '400ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-3 py-3 border-t" style={{ borderColor: 'rgba(0, 240, 255, 0.12)' }}>
        <div className="flex gap-2">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask Tim..."
            disabled={sending}
            data-testid="tiny-tim-input"
            className="flex-1 px-3 py-2 rounded-lg text-sm text-white placeholder-slate-500 outline-none"
            style={{
              background: 'rgba(0, 0, 0, 0.3)',
              border: '1px solid rgba(0, 240, 255, 0.15)',
            }}
          />
          <button
            onClick={sendMessage}
            disabled={sending || !input.trim()}
            data-testid="tiny-tim-send-btn"
            className="px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-30"
            style={{
              background: 'linear-gradient(135deg, rgba(0, 240, 255, 0.2), rgba(176, 38, 255, 0.2))',
              border: '1px solid rgba(0, 240, 255, 0.3)',
              color: '#00F0FF',
            }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TinyTimChat;
